/**
 * Comprehensive Service Content Data for all Finease Services
 * This file contains detailed content for all service pages
 */

// ==================== REGISTRATIONS ====================

export const msmeRegistration = {
  id: "msme-registration",
  slug: "msme-registration",
  seoTitle: "MSME/Udyam Registration Online | Get Government Benefits",
  seoDescription: "Register your Micro, Small or Medium Enterprise online. Get Udyam certificate, access to government subsidies, lower interest rates, and priority sector lending benefits.",
  heroTitle: "MSME/Udyam Registration",
  heroSubtitle: "Business Registration",
  summary: "MSME (Micro, Small and Medium Enterprises) Registration, now known as Udyam Registration, is a government certification that provides numerous benefits including priority sector lending, lower interest rates, protection against delayed payments, and access to various government subsidies and schemes. Registration is free and completely online through the Udyam portal.",
  
  whoNeeds: {
    title: "Who Should Register for MSME/Udyam?",
    description: "Any business engaged in manufacturing or providing services can register as MSME based on investment and turnover criteria.",
    points: [
      "Manufacturing units with investment up to ₹50 crore and turnover up to ₹250 crore",
      "Service enterprises with investment up to ₹50 crore and turnover up to ₹250 crore",
      "Proprietorships, partnerships, LLPs, and private limited companies",
      "Startups looking for government scheme benefits",
      "Businesses seeking collateral-free loans from banks",
      "Exporters needing export promotion benefits",
      "Businesses applying for government tenders with MSME reservation",
      "Enterprises seeking protection under MSMED Act against delayed payments"
    ]
  },
  
  whyRequired: {
    title: "Why is MSME Registration Important?",
    description: "MSME registration opens doors to numerous government benefits and provides legal protection for your business.",
    points: [
      "Collateral-Free Loans: Access to credit guarantee fund scheme for collateral-free loans up to ₹2 crore.",
      "Lower Interest Rates: Banks offer 1-1.5% lower interest rates on loans to registered MSMEs.",
      "Government Subsidies: Eligibility for various central and state government subsidy schemes.",
      "Priority Sector Lending: Banks are mandated to lend to MSMEs under priority sector targets.",
      "Delayed Payment Protection: Legal right to claim interest on delayed payments from buyers.",
      "Tender Preference: Many government tenders reserve 25% procurement for MSMEs."
    ]
  },
  
  benefits: [
    { title: "Credit Benefits", description: "Lower interest rates, collateral-free loans, and easier access to bank finance through priority sector lending." },
    { title: "Tax Benefits", description: "Various tax exemptions and subsidies including electricity duty exemption in many states." },
    { title: "Government Schemes", description: "Access to schemes like CLCSS, PMEGP, and various state-level incentive programs." },
    { title: "Payment Protection", description: "Interest on delayed payments from buyers under MSMED Act provisions." },
    { title: "Market Access", description: "Preference in government tenders with 25% procurement reserved for MSMEs." },
    { title: "ISO Certification", description: "Reimbursement of ISO certification fees under government schemes." },
    { title: "Trademark Subsidy", description: "50% subsidy on trademark registration fees for MSMEs." },
    { title: "Patent Support", description: "Financial assistance for patent registration under various schemes." }
  ],
  
  documentsRequired: {
    title: "Documents Required for MSME Registration",
    description: "Minimal documentation required for Udyam registration as it's Aadhaar-based.",
    directors: [
      "Aadhaar number of the proprietor/partner/director",
      "PAN card (linked with Aadhaar)",
      "Mobile number linked with Aadhaar",
      "Email ID for communication"
    ],
    company: [
      "Business PAN (for company/LLP)",
      "GSTIN (if registered under GST)",
      "Bank account details",
      "Business address proof",
      "Details of plant & machinery investment",
      "Previous year's turnover details"
    ]
  },
  
  process: [
    { step: 1, title: "Aadhaar Verification", description: "OTP-based Aadhaar verification of the proprietor/authorized signatory on the Udyam portal.", duration: "Instant" },
    { step: 2, title: "PAN Validation", description: "Business PAN is validated against income tax database automatically.", duration: "Instant" },
    { step: 3, title: "GST Details Auto-fetch", description: "If GST registered, details are auto-fetched from GSTN database.", duration: "Instant" },
    { step: 4, title: "Fill Enterprise Details", description: "Enter business name, type, address, NIC codes, investment, and turnover details.", duration: "10-15 mins" },
    { step: 5, title: "Submit Declaration", description: "Submit self-declaration on investment and turnover figures.", duration: "5 mins" },
    { step: 6, title: "Udyam Certificate", description: "Receive Udyam Registration Certificate with permanent URN immediately.", duration: "Instant" }
  ],
  
  timeline: {
    total: "Same Day",
    breakdown: [
      { phase: "Document preparation", duration: "30 mins" },
      { phase: "Online registration", duration: "15-20 mins" },
      { phase: "Certificate generation", duration: "Instant" }
    ],
    note: "Registration is free and instant through official Udyam portal. No fees charged by government."
  },
  
  pricing: {
    title: "MSME Registration Pricing",
    description: "We assist with the registration process ensuring all details are correctly filled.",
    packages: [
      {
        name: "Self-Filing Guide",
        price: "₹499",
        description: "Step-by-step guidance for self-registration",
        features: [
          "Detailed registration guide",
          "NIC code assistance",
          "Document checklist",
          "Phone support",
          "Verification assistance"
        ]
      },
      {
        name: "Assisted Filing",
        price: "₹999",
        description: "Complete filing by our experts",
        features: [
          "Complete registration process",
          "NIC code selection",
          "Investment calculation",
          "Turnover verification",
          "Certificate delivery"
        ],
        popular: true
      },
      {
        name: "Business Package",
        price: "₹2,999",
        description: "MSME + GST Registration combo",
        features: [
          "MSME registration",
          "GST registration",
          "Shop establishment",
          "Business bank account assistance",
          "Compliance calendar"
        ]
      }
    ],
    governmentFees: "MSME/Udyam registration is completely free. No government fees applicable."
  },
  
  faqs: [
    { question: "What is the difference between MSME and Udyam registration?", answer: "Udyam Registration is the new name for MSME Registration introduced in July 2020. The registration process is now completely online, paperless, and based on self-declaration. Existing EM-II and UAM registrations need to be migrated to Udyam." },
    { question: "Is MSME registration mandatory?", answer: "MSME registration is not mandatory but highly recommended to avail various government benefits, subsidies, and schemes. It's essential for participating in government tenders with MSME reservation." },
    { question: "What is the validity of Udyam Registration?", answer: "Udyam Registration is valid for lifetime and doesn't require renewal. However, you must update the registration if there are changes in investment or turnover that change your enterprise category." },
    { question: "Can a company register as MSME?", answer: "Yes, any business entity including proprietorship, partnership, LLP, private limited, or public limited company can register as MSME if they meet the investment and turnover criteria." },
    { question: "What are the MSME classification criteria?", answer: "Micro: Investment up to ₹1 crore and turnover up to ₹5 crore. Small: Investment up to ₹10 crore and turnover up to ₹50 crore. Medium: Investment up to ₹50 crore and turnover up to ₹250 crore." },
    { question: "Is GSTIN mandatory for MSME registration?", answer: "GSTIN is not mandatory for Udyam registration. However, if you have GST registration, the details will be auto-fetched and validated during the registration process." }
  ],
  
  relatedServices: [
    { id: "startup-india", label: "Startup India" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "shop-establishment", label: "Shop & Establishment" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "trade-license", label: "Trade License" },
    { id: "iso-certification", label: "ISO Certification" }
  ],
  
  trustSignals: {
    incorporations: "8,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const startupIndiaRegistration = {
  id: "startup-india",
  slug: "startup-india",
  seoTitle: "Startup India Registration | DPIIT Recognition Online",
  seoDescription: "Get DPIIT recognition under Startup India initiative. Access tax benefits, funding support, easier compliance, and government tender eligibility. Expert assistance for registration.",
  heroTitle: "Startup India Registration",
  heroSubtitle: "DPIIT Recognition",
  summary: "Startup India Registration provides DPIIT (Department for Promotion of Industry and Internal Trade) recognition to eligible startups, unlocking numerous benefits including income tax exemption for 3 years, exemption from Angel Tax, faster patent examination, easier public procurement norms, and access to Fund of Funds. Our experts help you navigate the recognition process efficiently.",
  
  whoNeeds: {
    title: "Who Can Register Under Startup India?",
    description: "Startups incorporated as Private Limited Company, LLP, or Partnership Firm working on innovation or scalable business models.",
    points: [
      "Companies or LLPs incorporated for less than 10 years",
      "Entities with annual turnover not exceeding ₹100 crore in any financial year",
      "Businesses working towards innovation, development, or improvement of products/services",
      "Entities with scalable business model with high potential for employment or wealth creation",
      "Businesses not formed by splitting or reconstruction of existing business",
      "Tech startups seeking faster patent examination benefits",
      "Startups planning to raise angel investment (Angel Tax exemption)",
      "Companies seeking self-certification for labor and environment laws"
    ]
  },
  
  whyRequired: {
    title: "Why Get Startup India Recognition?",
    description: "DPIIT recognition provides significant advantages for startups in their growth journey.",
    points: [
      "Tax Holiday: 100% income tax exemption for 3 consecutive years out of first 10 years from incorporation.",
      "Angel Tax Exemption: Investments above fair market value not taxed as income for DPIIT recognized startups.",
      "Self-Certification: Compliance through self-certification for 6 labor laws and 3 environmental laws.",
      "Fast-Track Patents: 80% rebate on patent filing fees and expedited examination process.",
      "Government Tenders: Relaxation in prior experience/turnover requirements for public procurement.",
      "Fund of Funds: Access to ₹10,000 crore Fund of Funds managed by SIDBI for equity funding."
    ]
  },
  
  benefits: [
    { title: "3-Year Tax Holiday", description: "100% income tax exemption for any 3 consecutive years out of first 10 years." },
    { title: "Angel Tax Exemption", description: "Protection from Section 56(2)(viib) - investments above FMV not taxed." },
    { title: "Patent Fast-Track", description: "80% rebate on patent fees and expedited examination within 6-8 months." },
    { title: "Self-Certification", description: "No inspections for labor and environmental compliance for 5 years." },
    { title: "Easy Public Procurement", description: "Relaxed norms for participating in government tenders." },
    { title: "Fund Access", description: "Eligibility for Fund of Funds, SIDBI schemes, and credit guarantee." },
    { title: "Mentor Support", description: "Access to Hub mentors and industry experts through Startup India network." },
    { title: "Networking", description: "Platform to connect with investors, accelerators, and other startups." }
  ],
  
  documentsRequired: {
    title: "Documents for Startup India Registration",
    description: "Documentation varies based on entity type and nature of innovation.",
    directors: [
      "Directors/Partners identification documents",
      "Authorization letter for authorized representative",
      "Contact details of all founders"
    ],
    company: [
      "Certificate of Incorporation/Registration",
      "PAN of the entity",
      "Brief description of innovation/business idea",
      "Proof of concept or prototype (if available)",
      "Awards, patents, or IP (if any)",
      "Recommendation letter from incubator (if applicable)",
      "Investment proof (for angel tax exemption)"
    ]
  },
  
  process: [
    { step: 1, title: "Create Account", description: "Register on Startup India portal with email verification and profile setup.", duration: "10 mins" },
    { step: 2, title: "Fill Application", description: "Complete the recognition application with business details and innovation description.", duration: "30 mins" },
    { step: 3, title: "Upload Documents", description: "Submit incorporation certificate, brief, and supporting documents.", duration: "15 mins" },
    { step: 4, title: "Self-Declaration", description: "Provide self-declaration on eligibility criteria and no splitting/reconstruction.", duration: "5 mins" },
    { step: 5, title: "DPIIT Review", description: "Application is reviewed by DPIIT Inter-Ministerial Board.", duration: "2-5 days" },
    { step: 6, title: "Recognition Certificate", description: "Receive DPIIT Recognition Certificate and unique recognition number.", duration: "On approval" }
  ],
  
  timeline: {
    total: "3-7 business days",
    breakdown: [
      { phase: "Application preparation", duration: "1 day" },
      { phase: "Document submission", duration: "Same day" },
      { phase: "DPIIT review", duration: "2-5 days" },
      { phase: "Certificate issuance", duration: "Instant on approval" }
    ],
    note: "Timeline depends on application completeness and DPIIT processing. Clear innovation description speeds up approval."
  },
  
  pricing: {
    title: "Startup India Registration Fees",
    description: "Professional assistance to maximize your chances of approval.",
    packages: [
      {
        name: "Basic Filing",
        price: "₹2,999",
        description: "For straightforward applications",
        features: [
          "Application preparation",
          "Document review",
          "Portal submission",
          "Application tracking",
          "Certificate delivery"
        ]
      },
      {
        name: "Complete Support",
        price: "₹5,999",
        description: "End-to-end registration support",
        features: [
          "Innovation brief drafting",
          "All documentation",
          "Application filing",
          "Query handling",
          "Tax exemption application"
        ],
        popular: true
      },
      {
        name: "Startup Package",
        price: "₹14,999",
        description: "Complete startup foundation",
        features: [
          "Company incorporation",
          "Startup India registration",
          "MSME registration",
          "Tax planning consultation",
          "Investor pitch deck review"
        ]
      }
    ],
    governmentFees: "Startup India registration on DPIIT portal is completely free."
  },
  
  faqs: [
    { question: "What is the eligibility criteria for Startup India?", answer: "Entity should be a Pvt Ltd, LLP, or Partnership, incorporated for less than 10 years, with turnover under ₹100 crore. Must be working on innovation/improvement of products/services with potential for scalability." },
    { question: "Can existing businesses register under Startup India?", answer: "Yes, if the entity is less than 10 years old, has turnover under ₹100 crore, and is working on innovative products/services. The entity should not be formed by splitting an existing business." },
    { question: "What is the tax exemption process?", answer: "After DPIIT recognition, apply separately for tax exemption through Form 1 to Inter-Ministerial Board. If approved, you can claim 100% tax exemption for 3 consecutive years." },
    { question: "How to get Angel Tax exemption?", answer: "DPIIT recognized startups filing Form 2 and meeting criteria (aggregate paid-up capital under ₹25 crore) get automatic exemption from Section 56(2)(viib)." },
    { question: "Is Startup India different from MSME registration?", answer: "Yes, they are different registrations with different benefits. Many startups qualify for both. Startup India focuses on innovation while MSME is based on investment and turnover." },
    { question: "Can a sole proprietorship register under Startup India?", answer: "No, only Private Limited Companies, LLPs, and Registered Partnership Firms can apply for Startup India recognition. Proprietorships should first incorporate as a company or LLP." }
  ],
  
  relatedServices: [
    { id: "private-limited-company", label: "Company Registration" },
    { id: "llp-registration", label: "LLP Registration" },
    { id: "msme-registration", label: "MSME Registration" },
    { id: "patent-registration", label: "Patent Registration" },
    { id: "trademark-registration", label: "Trademark Registration" },
    { id: "virtual-cfo", label: "Virtual CFO" }
  ],
  
  trustSignals: {
    incorporations: "3,500+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const shopEstablishment = {
  id: "shop-establishment",
  slug: "shop-establishment",
  seoTitle: "Shop & Establishment License | Registration Online",
  seoDescription: "Get Shop & Establishment registration for your business. State-wise license for shops, offices, restaurants, and commercial establishments. Quick registration with expert help.",
  heroTitle: "Shop & Establishment Registration",
  heroSubtitle: "State License",
  summary: "Shop & Establishment Registration is a mandatory state-level registration required for all commercial establishments including shops, offices, restaurants, hotels, and warehouses. It regulates working hours, employee welfare, holidays, and working conditions. Our experts handle registrations across all states with quick turnaround.",
  
  whoNeeds: {
    title: "Who Requires Shop & Establishment License?",
    description: "All commercial establishments employing one or more persons need this registration.",
    points: [
      "Retail shops and showrooms",
      "Offices and commercial establishments",
      "Restaurants, cafes, and food establishments",
      "Hotels and lodging businesses",
      "Warehouses and storage facilities",
      "Entertainment establishments",
      "Healthcare clinics and diagnostic centers",
      "Professional service offices (CA, lawyers, consultants)"
    ]
  },
  
  whyRequired: {
    title: "Why is Shop & Establishment Registration Mandatory?",
    description: "This registration provides legal recognition and ensures compliance with labor welfare laws.",
    points: [
      "Legal Recognition: Official authorization to operate a commercial establishment in the state.",
      "Labor Compliance: Demonstrates compliance with working hours, holidays, and employee welfare regulations.",
      "Bank Requirements: Often required for opening business bank accounts and obtaining loans.",
      "GST Registration: Serves as proof of business premises for GST registration.",
      "Other Licenses: Required as supporting document for FSSAI, liquor, and other licenses.",
      "Avoid Penalties: Non-registration can result in fines up to ₹50,000 and business closure."
    ]
  },
  
  benefits: [
    { title: "Legal Operation", description: "Authorized to operate commercial establishment within the state legally." },
    { title: "Employee Protection", description: "Framework for working hours, leave, and employee welfare compliance." },
    { title: "Bank Account", description: "Facilitates opening of current account and availing business loans." },
    { title: "GST Support", description: "Valid address proof for GST registration and other licenses." },
    { title: "Inspection Ready", description: "Protection from raids and penalties by labor department." },
    { title: "Government Tenders", description: "Required document for participating in government contracts." },
    { title: "Professional Image", description: "Enhances credibility with customers and business partners." },
    { title: "Insurance", description: "Required for obtaining commercial insurance policies." }
  ],
  
  documentsRequired: {
    title: "Documents for Shop & Establishment",
    description: "Documentation requirements vary by state. Common requirements listed below.",
    directors: [
      "PAN card of proprietor/partners/directors",
      "Aadhaar card or identity proof",
      "Passport-size photographs",
      "Mobile number and email ID"
    ],
    company: [
      "Rent agreement or ownership proof of premises",
      "NOC from landlord (if rented)",
      "Electricity bill of premises",
      "Business PAN (if company/LLP)",
      "Partnership deed (if partnership)",
      "Certificate of Incorporation (if company)",
      "Employee details (name, address, salary)"
    ]
  },
  
  process: [
    { step: 1, title: "Document Collection", description: "Gather all required documents based on state requirements and business type.", duration: "1 day" },
    { step: 2, title: "Application Form", description: "Fill the state-specific application form with business and employee details.", duration: "30 mins" },
    { step: 3, title: "Fee Payment", description: "Pay the registration fees based on number of employees and business category.", duration: "Instant" },
    { step: 4, title: "Submission", description: "Submit application online or at municipal/labor office as per state process.", duration: "Same day" },
    { step: 5, title: "Verification", description: "Inspector may verify premises (physical inspection varies by state).", duration: "1-7 days" },
    { step: 6, title: "Certificate Issuance", description: "Receive Shop & Establishment Registration Certificate.", duration: "3-15 days" }
  ],
  
  timeline: {
    total: "3-15 business days",
    breakdown: [
      { phase: "Document preparation", duration: "1 day" },
      { phase: "Application submission", duration: "1 day" },
      { phase: "Processing time", duration: "3-15 days" }
    ],
    note: "Timeline varies significantly by state. Some states offer instant registration while others require physical verification."
  },
  
  pricing: {
    title: "Shop & Establishment Fees",
    description: "Our fees include complete filing assistance. Government fees vary by state and employee count.",
    packages: [
      {
        name: "Basic",
        price: "₹999",
        description: "For establishments with up to 5 employees",
        features: [
          "Application preparation",
          "Document review",
          "Online/offline submission",
          "Follow-up with authorities",
          "Certificate delivery"
        ]
      },
      {
        name: "Standard",
        price: "₹1,999",
        description: "For establishments with 6-20 employees",
        features: [
          "Everything in Basic",
          "Multiple branch registration",
          "Amendment assistance",
          "Annual renewal reminder",
          "Compliance guidance"
        ],
        popular: true
      },
      {
        name: "Enterprise",
        price: "₹4,999",
        description: "For larger establishments",
        features: [
          "Everything in Standard",
          "PAN India registration",
          "Multi-state compliance",
          "Labor law consultation",
          "Dedicated support"
        ]
      }
    ],
    governmentFees: "Government fees range from ₹200 to ₹5,000 depending on state, employee count, and establishment category."
  },
  
  faqs: [
    { question: "Is Shop & Establishment registration mandatory?", answer: "Yes, it's mandatory for all commercial establishments in India. The requirement is governed by respective state's Shop & Establishment Act." },
    { question: "What is the validity of Shop & Establishment license?", answer: "Validity varies by state - typically 1 to 5 years. Some states have introduced lifetime registration with annual fees." },
    { question: "Do I need separate registration for each branch?", answer: "Yes, each business location requires separate registration under that state's jurisdiction." },
    { question: "Can I register online?", answer: "Most states now offer online registration. Some states still require physical submission. We handle both processes." },
    { question: "What are the penalties for non-registration?", answer: "Penalties vary by state but can include fines up to ₹50,000, daily penalties for continued violation, and in severe cases, business closure orders." },
    { question: "Is this different from Trade License?", answer: "Yes, Shop & Establishment is state-level for labor compliance. Trade License is municipal-level for business operation permission. Both may be required." }
  ],
  
  relatedServices: [
    { id: "trade-license", label: "Trade License" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "msme-registration", label: "MSME Registration" },
    { id: "fssai-license", label: "FSSAI License" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "payroll-compliance", label: "Payroll Compliance" }
  ],
  
  trustSignals: {
    incorporations: "12,000+",
    rating: "4.7/5",
    experience: "10+ Years",
    support: "PAN India"
  }
};

export const tradeLicense = {
  id: "trade-license",
  slug: "trade-license",
  seoTitle: "Trade License Registration | Municipal Business License",
  seoDescription: "Get Trade License from municipal corporation for your business. Required for operating any trade or business within city limits. Quick processing with expert assistance.",
  heroTitle: "Trade License Registration",
  heroSubtitle: "Municipal License",
  summary: "Trade License is a mandatory municipal-level permission required to carry on any trade, business, or profession within a city or town. It ensures businesses operate in compliance with local regulations, zoning laws, and health & safety standards. Our team handles trade license applications across all municipalities.",
  
  whoNeeds: {
    title: "Who Needs a Trade License?",
    description: "Any business operating within municipal limits requires a trade license from the local authority.",
    points: [
      "Shops and retail businesses",
      "Restaurants and food service establishments",
      "Manufacturing units within city limits",
      "Service businesses and professional offices",
      "Warehouses and godowns",
      "Hotels and guest houses",
      "Educational and coaching institutes",
      "Healthcare establishments"
    ]
  },
  
  whyRequired: {
    title: "Why is Trade License Important?",
    description: "Trade license is the primary authorization for conducting business in a locality.",
    points: [
      "Legal Authorization: Permits legal operation of business within municipal jurisdiction.",
      "Health & Safety: Ensures compliance with sanitation and safety standards.",
      "Zoning Compliance: Verifies business is operating in permitted zone/area.",
      "Bank Requirements: Required for business bank account and loan applications.",
      "Other Licenses: Prerequisite for obtaining other permits like FSSAI, liquor license.",
      "Avoid Closure: Operating without license can lead to sealing of premises and heavy fines."
    ]
  },
  
  benefits: [
    { title: "Legal Operation", description: "Authorization to conduct business within city/town municipal limits." },
    { title: "Credibility", description: "Establishes business legitimacy with customers and authorities." },
    { title: "Bank Support", description: "Facilitates business loans and current account opening." },
    { title: "License Support", description: "Required for obtaining FSSAI, bar, and other licenses." },
    { title: "Protection", description: "Safeguards against municipal raids and business closure." },
    { title: "Tenders", description: "Required document for government and corporate contracts." },
    { title: "Insurance", description: "Essential for commercial property and liability insurance." },
    { title: "Renewal Record", description: "Creates compliance track record for business." }
  ],
  
  documentsRequired: {
    title: "Documents for Trade License",
    description: "Requirements vary by municipality. Common documents include:",
    directors: [
      "Identity proof (Aadhaar/Voter ID/Passport)",
      "PAN card of applicant",
      "Passport-size photographs",
      "Address proof of applicant"
    ],
    company: [
      "Proof of premises (rent agreement/sale deed)",
      "NOC from landlord if rented",
      "Property tax receipt",
      "Building plan approval (for new construction)",
      "Business registration documents",
      "Partnership deed/MOA as applicable",
      "Fire NOC (for applicable categories)"
    ]
  },
  
  process: [
    { step: 1, title: "Document Preparation", description: "Compile all required documents as per municipal requirements.", duration: "1 day" },
    { step: 2, title: "Application Filing", description: "Submit application form with documents to municipal corporation.", duration: "1 day" },
    { step: 3, title: "Fee Payment", description: "Pay applicable trade license fees based on business category.", duration: "Same day" },
    { step: 4, title: "Site Inspection", description: "Municipal officer verifies premises for compliance.", duration: "3-10 days" },
    { step: 5, title: "NOC Collection", description: "Obtain NOCs from health, fire departments if required.", duration: "Variable" },
    { step: 6, title: "License Issuance", description: "Receive Trade License certificate from municipality.", duration: "On approval" }
  ],
  
  timeline: {
    total: "7-30 business days",
    breakdown: [
      { phase: "Document preparation", duration: "1-2 days" },
      { phase: "Application submission", duration: "1 day" },
      { phase: "Inspection & verification", duration: "5-20 days" },
      { phase: "License issuance", duration: "3-7 days" }
    ],
    note: "Timeline varies significantly based on municipality, business type, and inspection requirements."
  },
  
  pricing: {
    title: "Trade License Fees",
    description: "Professional fees for complete application handling.",
    packages: [
      {
        name: "Basic",
        price: "₹1,499",
        description: "Standard trade license filing",
        features: [
          "Application preparation",
          "Document compilation",
          "Municipal submission",
          "Follow-up with authorities",
          "License collection"
        ]
      },
      {
        name: "Express",
        price: "₹2,999",
        description: "Priority processing",
        features: [
          "Everything in Basic",
          "Priority filing",
          "Inspection coordination",
          "NOC assistance",
          "Faster processing"
        ],
        popular: true
      },
      {
        name: "Complete",
        price: "₹5,999",
        description: "All municipal compliances",
        features: [
          "Trade license",
          "Shop establishment",
          "Signboard permission",
          "Health NOC assistance",
          "Fire NOC coordination"
        ]
      }
    ],
    governmentFees: "Municipal fees range from ₹500 to ₹25,000+ based on trade category, area, and city."
  },
  
  faqs: [
    { question: "Is trade license same as Shop & Establishment?", answer: "No, Trade License is from municipal corporation for permission to trade. Shop & Establishment is from labor department for employee welfare compliance. Both are usually required." },
    { question: "What's the validity of trade license?", answer: "Typically valid for one year from April to March. Some municipalities now offer longer validity periods. Annual renewal is mandatory." },
    { question: "Can I start business before getting trade license?", answer: "Technically you need the license before starting, but many municipalities allow operation during the application process. Operating without license risks penalties." },
    { question: "What happens if I don't renew trade license?", answer: "Operating with expired license attracts penalties. Continued default can lead to sealing of premises and higher renewal fees with penalties." },
    { question: "Do home-based businesses need trade license?", answer: "Requirements vary by municipality and residential zone restrictions. Small home-based services may get exemptions, but it's best to check local rules." },
    { question: "Is separate license needed for each branch?", answer: "Yes, trade license is location-specific. Each business premises requires separate registration with that area's municipal authority." }
  ],
  
  relatedServices: [
    { id: "shop-establishment", label: "Shop & Establishment" },
    { id: "fssai-license", label: "FSSAI License" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "msme-registration", label: "MSME Registration" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "fire-noc", label: "Fire NOC" }
  ],
  
  trustSignals: {
    incorporations: "10,000+",
    rating: "4.6/5",
    experience: "10+ Years",
    support: "PAN India"
  }
};

// ==================== NGO REGISTRATIONS ====================

export const trustRegistration = {
  id: "trust-registration",
  slug: "trust-registration",
  seoTitle: "Trust Registration in India | Public Charitable Trust",
  seoDescription: "Register your charitable or private trust. Expert assistance for trust deed drafting, registration, and 12A/80G certification. Complete NGO setup services.",
  heroTitle: "Trust Registration",
  heroSubtitle: "NGO Registration",
  summary: "Trust Registration creates a legal entity for charitable, religious, or educational purposes. A Trust is governed by the Indian Trusts Act, 1882 and state-specific Public Trusts Acts. We provide end-to-end assistance from trust deed drafting to registration and tax exemption certifications.",
  
  whoNeeds: {
    title: "Who Should Register a Trust?",
    description: "Trusts are ideal for individuals and groups wanting to establish charitable or religious institutions.",
    points: [
      "Individuals establishing charitable organizations",
      "Groups setting up educational institutions",
      "Religious organizations and temples",
      "Healthcare and medical charity initiatives",
      "Environmental and social welfare organizations",
      "Family trusts for succession planning",
      "Corporate CSR foundations",
      "Community welfare organizations"
    ]
  },
  
  whyRequired: {
    title: "Why Register a Trust?",
    description: "Trust registration provides legal recognition and enables tax-efficient charitable work.",
    points: [
      "Legal Entity: Trust becomes a recognized legal entity that can own property and enter contracts.",
      "Tax Benefits: Registered trusts can apply for 12A and 80G certifications for tax exemptions.",
      "Credibility: Formal registration enhances trust among donors and beneficiaries.",
      "Perpetuity: Trust continues regardless of changes in trustees, ensuring long-term impact.",
      "Asset Protection: Clear framework for managing and protecting trust assets.",
      "Donor Confidence: 80G registration allows donors to claim tax deductions on contributions."
    ]
  },
  
  benefits: [
    { title: "Legal Recognition", description: "Trust becomes a legal entity capable of holding property and assets." },
    { title: "Tax Exemption", description: "Eligible for 12A registration exempting income from tax." },
    { title: "Donor Benefits", description: "80G registration provides tax benefits to donors." },
    { title: "Perpetual Existence", description: "Trust continues indefinitely beyond trustees' tenure." },
    { title: "CSR Eligibility", description: "Registered trusts can receive corporate CSR funds." },
    { title: "Grant Eligibility", description: "Can apply for government and international grants." },
    { title: "FCRA Potential", description: "Can apply for FCRA to receive foreign contributions." },
    { title: "Credibility", description: "Formal structure builds donor and public confidence." }
  ],
  
  documentsRequired: {
    title: "Documents for Trust Registration",
    description: "Documentation requirements for establishing a public charitable trust.",
    directors: [
      "ID proof of all trustees (Aadhaar/PAN/Passport)",
      "Address proof of all trustees",
      "Passport-size photographs",
      "PAN card of all trustees"
    ],
    company: [
      "Trust deed (we help draft this)",
      "Registered office address proof",
      "NOC from property owner",
      "Objectives of the trust",
      "Initial corpus amount (minimum ₹1,000)",
      "Details of trustees (minimum 2)"
    ]
  },
  
  process: [
    { step: 1, title: "Trust Deed Drafting", description: "Our experts draft the trust deed with objectives, trustee details, and operational provisions.", duration: "2-3 days" },
    { step: 2, title: "Trustee Meeting", description: "All trustees sign the trust deed in presence of witnesses.", duration: "1 day" },
    { step: 3, title: "Stamp Duty Payment", description: "Pay applicable stamp duty based on state and corpus amount.", duration: "1 day" },
    { step: 4, title: "Sub-Registrar Filing", description: "Submit trust deed for registration at local sub-registrar office.", duration: "1 day" },
    { step: 5, title: "Registration Verification", description: "Sub-registrar verifies documents and trustee identities.", duration: "7-15 days" },
    { step: 6, title: "Registered Trust Deed", description: "Receive the registered trust deed with registration number.", duration: "On completion" }
  ],
  
  timeline: {
    total: "15-30 days",
    breakdown: [
      { phase: "Trust deed drafting", duration: "2-3 days" },
      { phase: "Stamp duty & documentation", duration: "2-3 days" },
      { phase: "Sub-registrar processing", duration: "10-20 days" },
      { phase: "Registration completion", duration: "1-3 days" }
    ],
    note: "Timeline depends on state procedures and document readiness. 12A/80G applications are separate processes."
  },
  
  pricing: {
    title: "Trust Registration Pricing",
    description: "Comprehensive packages for trust establishment and compliance.",
    packages: [
      {
        name: "Basic Trust",
        price: "₹9,999",
        description: "Trust registration only",
        features: [
          "Trust deed drafting",
          "Sub-registrar filing",
          "Registration completion",
          "PAN application",
          "Bank account assistance"
        ]
      },
      {
        name: "Tax Exempt Trust",
        price: "₹19,999",
        description: "Trust + 12A + 80G",
        features: [
          "Everything in Basic",
          "12A registration",
          "80G registration",
          "Income tax filing (1 year)",
          "Compliance calendar"
        ],
        popular: true
      },
      {
        name: "Complete NGO",
        price: "₹34,999",
        description: "Full NGO setup",
        features: [
          "Everything in Tax Exempt",
          "Darpan registration",
          "CSR-1 registration",
          "Website basic setup",
          "Annual compliance (1 year)"
        ]
      }
    ],
    governmentFees: "Stamp duty ranges from ₹500 to ₹5,000+ depending on state and corpus amount. Registration fees approximately ₹500-2,000."
  },
  
  faqs: [
    { question: "What is the minimum number of trustees required?", answer: "Minimum 2 trustees are required. There's no maximum limit. It's advisable to have odd numbers (3, 5, 7) to facilitate decision-making." },
    { question: "Can a trust do business activities?", answer: "Charitable trusts should primarily engage in charitable activities. Limited incidental business is allowed if profits go towards charitable objectives." },
    { question: "What's the difference between trust and society?", answer: "Trusts are managed by trustees with more founder control. Societies are membership-based with democratic structure. Choice depends on governance preference." },
    { question: "How long is trust registration valid?", answer: "Trust registration is permanent - valid for the lifetime of the trust. Annual compliance filings are required." },
    { question: "What is 12A and 80G registration?", answer: "12A exempts trust income from tax. 80G allows donors to claim tax deductions on donations. Both are separate applications after trust registration." },
    { question: "Can NRIs be trustees?", answer: "Yes, NRIs can be trustees. However, for operational efficiency, at least one resident trustee is recommended." }
  ],
  
  relatedServices: [
    { id: "society-registration", label: "Society Registration" },
    { id: "section-8-company", label: "Section 8 Company" },
    { id: "ngo-compliance", label: "NGO Compliance" },
    { id: "fcra-registration", label: "FCRA Registration" },
    { id: "csr-registration", label: "CSR Registration" },
    { id: "income-tax-filing", label: "ITR Filing" }
  ],
  
  trustSignals: {
    incorporations: "2,500+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const societyRegistration = {
  id: "society-registration",
  slug: "society-registration",
  seoTitle: "Society Registration | Societies Registration Act 1860",
  seoDescription: "Register your society under Societies Registration Act 1860. Ideal for clubs, associations, and membership-based NGOs. Expert assistance for registration and compliance.",
  heroTitle: "Society Registration",
  heroSubtitle: "NGO Registration",
  summary: "Society Registration under the Societies Registration Act, 1860 creates a legal entity for charitable, scientific, literary, or recreational purposes. Societies are membership-based organizations with democratic governance. Ideal for clubs, associations, NGOs, and community organizations.",
  
  whoNeeds: {
    title: "Who Should Register a Society?",
    description: "Societies are suited for membership-based organizations requiring democratic governance.",
    points: [
      "Educational and literary organizations",
      "Scientific and research institutions",
      "Social and cultural clubs",
      "Sports and recreation associations",
      "Professional and trade associations",
      "Resident welfare associations (RWAs)",
      "Alumni associations",
      "Charitable and welfare organizations"
    ]
  },
  
  whyRequired: {
    title: "Why Register a Society?",
    description: "Society registration provides legal framework for membership-based organizations.",
    points: [
      "Legal Entity: Society can own property, sue, and be sued in its own name.",
      "Democratic Governance: Members elect governing body ensuring accountability.",
      "Tax Benefits: Eligible for 12A and 80G registrations for tax exemptions.",
      "Perpetuity: Society continues regardless of changes in membership.",
      "Grant Eligibility: Can receive government grants and CSR funds.",
      "Credibility: Registered status enhances credibility with stakeholders."
    ]
  },
  
  benefits: [
    { title: "Legal Status", description: "Society becomes a legal entity distinct from its members." },
    { title: "Democratic Control", description: "Members have voting rights in organization's governance." },
    { title: "Tax Exemption", description: "Eligible for 12A and 80G tax exemption certificates." },
    { title: "Perpetual Succession", description: "Society continues beyond founding members." },
    { title: "Property Rights", description: "Can own and manage property in society's name." },
    { title: "Grant Eligible", description: "Can receive government and international funding." },
    { title: "FCRA Potential", description: "Eligible to apply for foreign contribution license." },
    { title: "Limited Liability", description: "Members' personal assets protected from society liabilities." }
  ],
  
  documentsRequired: {
    title: "Documents for Society Registration",
    description: "Documentation for registering under Societies Registration Act.",
    directors: [
      "ID proof of all governing body members",
      "Address proof of all members",
      "Passport-size photographs",
      "PAN cards of all members"
    ],
    company: [
      "Memorandum of Association",
      "Rules & Regulations (Bye-laws)",
      "Address proof of registered office",
      "NOC from property owner",
      "Resolution for society formation",
      "List of governing body members",
      "List of general body members (minimum 7)"
    ]
  },
  
  process: [
    { step: 1, title: "MOA Drafting", description: "Draft Memorandum of Association with society objectives and governing rules.", duration: "2-3 days" },
    { step: 2, title: "Bye-laws Preparation", description: "Prepare rules and regulations for society operations and membership.", duration: "2-3 days" },
    { step: 3, title: "Member Meeting", description: "Hold formation meeting with minimum 7 members to approve documents.", duration: "1 day" },
    { step: 4, title: "Application Filing", description: "Submit application with MOA, bye-laws, and member details to Registrar.", duration: "1 day" },
    { step: 5, title: "Verification", description: "Registrar verifies documents and may seek clarifications.", duration: "15-30 days" },
    { step: 6, title: "Registration Certificate", description: "Receive Society Registration Certificate with unique number.", duration: "On approval" }
  ],
  
  timeline: {
    total: "30-60 days",
    breakdown: [
      { phase: "Document preparation", duration: "5-7 days" },
      { phase: "Application submission", duration: "1-2 days" },
      { phase: "Registrar processing", duration: "15-45 days" },
      { phase: "Certificate issuance", duration: "7-15 days" }
    ],
    note: "Timeline varies by state. Delhi, Maharashtra, Karnataka typically process faster than other states."
  },
  
  pricing: {
    title: "Society Registration Fees",
    description: "Complete society setup and registration services.",
    packages: [
      {
        name: "Basic",
        price: "₹12,999",
        description: "Society registration only",
        features: [
          "MOA drafting",
          "Bye-laws preparation",
          "Registration filing",
          "PAN application",
          "Registration certificate"
        ]
      },
      {
        name: "Tax Exempt",
        price: "₹24,999",
        description: "Society + Tax registrations",
        features: [
          "Everything in Basic",
          "12A registration",
          "80G registration",
          "TAN registration",
          "First year compliance"
        ],
        popular: true
      },
      {
        name: "Complete NGO",
        price: "₹39,999",
        description: "Full NGO infrastructure",
        features: [
          "Everything in Tax Exempt",
          "Darpan registration",
          "CSR-1 registration",
          "FCRA consultation",
          "Annual compliance (1 year)"
        ]
      }
    ],
    governmentFees: "Registration fees vary by state - typically ₹500 to ₹5,000. Additional fees for certified copies."
  },
  
  faqs: [
    { question: "How many members are required to form a society?", answer: "Minimum 7 members are required to form a society. For societies operating in multiple states, minimum 8 members from 8 different states needed." },
    { question: "What's the difference between society and trust?", answer: "Society is membership-based with democratic elections. Trust is managed by trustees appointed by settlor. Society has more member control while trust has more founder control." },
    { question: "Can a society do commercial activities?", answer: "Societies can undertake limited commercial activities if income is used for society objectives. Primary purpose should remain charitable/non-profit." },
    { question: "What annual compliances are required?", answer: "Annual requirements include: list of managing committee members, audited accounts, income tax return, and renewal with Registrar if required by state." },
    { question: "Can society be converted to other forms?", answer: "Yes, societies can be converted to Section 8 companies. The process requires member approval and regulatory permissions." },
    { question: "What is the validity of society registration?", answer: "Registration is permanent but some states require annual renewal/filing. Regular compliance maintains active status." }
  ],
  
  relatedServices: [
    { id: "trust-registration", label: "Trust Registration" },
    { id: "section-8-company", label: "Section 8 Company" },
    { id: "ngo-compliance", label: "NGO Compliance" },
    { id: "fcra-registration", label: "FCRA Registration" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "ngo-audit", label: "NGO Audit" }
  ],
  
  trustSignals: {
    incorporations: "1,800+",
    rating: "4.7/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const section8Company = {
  id: "section-8-company",
  slug: "section-8-company",
  seoTitle: "Section 8 Company Registration | Non-Profit Company India",
  seoDescription: "Register Section 8 Company for non-profit activities. Get MCA license for charitable company with limited liability. Professional incorporation services.",
  heroTitle: "Section 8 Company Registration",
  heroSubtitle: "Non-Profit Company",
  summary: "Section 8 Company under Companies Act, 2013 is a non-profit organization registered as a limited company with MCA. It offers the structure and credibility of a company while pursuing charitable objectives. Ideal for established NGOs, corporate foundations, and large-scale social enterprises.",
  
  whoNeeds: {
    title: "Who Should Register Section 8 Company?",
    description: "Section 8 is ideal for NGOs requiring corporate structure and enhanced credibility.",
    points: [
      "Large-scale charitable organizations",
      "Corporate CSR foundations",
      "Educational and research institutions",
      "Healthcare and medical foundations",
      "Professional associations and bodies",
      "International NGOs establishing India presence",
      "Organizations seeking FCRA registration",
      "NGOs planning significant donor engagement"
    ]
  },
  
  whyRequired: {
    title: "Why Choose Section 8 Company?",
    description: "Section 8 companies combine nonprofit purpose with corporate governance benefits.",
    points: [
      "Limited Liability: Members protected from company's financial obligations.",
      "Credibility: Company structure inspires confidence among donors and grantors.",
      "No Minimum Capital: Unlike regular companies, no minimum capital required.",
      "Name Flexibility: Can use names without 'Limited' or 'Private Limited' suffix.",
      "Tax Benefits: Eligible for 12A, 80G, and other tax exemptions.",
      "FCRA Preference: Often preferred structure for FCRA registration approval."
    ]
  },
  
  benefits: [
    { title: "Corporate Structure", description: "Professional governance with board of directors and accountability." },
    { title: "Limited Liability", description: "Members' personal assets protected from company obligations." },
    { title: "No Minimum Capital", description: "Can be incorporated without any minimum paid-up capital requirement." },
    { title: "Name Benefits", description: "Can omit 'Ltd/Pvt Ltd' suffix, appearing more like traditional NGO." },
    { title: "Tax Exemption", description: "Eligible for 12A, 80G, and CSR-1 registrations." },
    { title: "FCRA Friendly", description: "Preferred structure for receiving foreign contributions." },
    { title: "Perpetual Existence", description: "Company continues regardless of member changes." },
    { title: "Asset Protection", description: "Clear framework for managing organizational assets." }
  ],
  
  documentsRequired: {
    title: "Documents for Section 8 Company",
    description: "Comprehensive documentation required for MCA licensing.",
    directors: [
      "PAN card of all directors (minimum 2)",
      "Aadhaar card/Voter ID/Passport",
      "Address proof (bank statement/utility bill)",
      "Passport-size photographs",
      "Director's consent (DIR-2)"
    ],
    company: [
      "Proposed company names (2-3 options)",
      "Detailed objects clause",
      "Registered office address proof",
      "NOC from property owner",
      "Estimated income-expenditure for 3 years",
      "Details of proposed activities",
      "Declaration of charitable objectives"
    ]
  },
  
  process: [
    { step: 1, title: "DSC & DIN", description: "Obtain Digital Signature Certificate and Director Identification Number for all directors.", duration: "2-3 days" },
    { step: 2, title: "Name Approval", description: "Apply for name availability through RUN (Reserve Unique Name) service.", duration: "2-4 days" },
    { step: 3, title: "License Application", description: "File INC-12 for Section 8 license with MOA, AOA, and financial projections.", duration: "1-2 days" },
    { step: 4, title: "MCA Review", description: "Regional Director reviews application and may seek clarifications.", duration: "30-45 days" },
    { step: 5, title: "License Grant", description: "Upon approval, MCA grants license to incorporate Section 8 company.", duration: "On approval" },
    { step: 6, title: "Incorporation", description: "File SPICe+ for incorporation and receive Certificate of Incorporation.", duration: "7-10 days" }
  ],
  
  timeline: {
    total: "45-90 days",
    breakdown: [
      { phase: "DSC & DIN procurement", duration: "3-5 days" },
      { phase: "Name approval", duration: "3-5 days" },
      { phase: "License application & review", duration: "30-60 days" },
      { phase: "Incorporation filing", duration: "7-15 days" }
    ],
    note: "Section 8 takes longer than regular companies due to license approval requirement. Clear objectives speed up approval."
  },
  
  pricing: {
    title: "Section 8 Company Pricing",
    description: "End-to-end Section 8 company incorporation services.",
    packages: [
      {
        name: "Essential",
        price: "₹24,999",
        description: "Section 8 incorporation only",
        features: [
          "DSC for 2 directors",
          "DIN procurement",
          "Name approval",
          "License application",
          "Incorporation filing",
          "PAN & TAN"
        ]
      },
      {
        name: "Complete",
        price: "₹44,999",
        description: "Incorporation + Tax registrations",
        features: [
          "Everything in Essential",
          "12A registration",
          "80G registration",
          "Darpan registration",
          "First AGM compliance"
        ],
        popular: true
      },
      {
        name: "Enterprise",
        price: "₹74,999",
        description: "Full NGO foundation",
        features: [
          "Everything in Complete",
          "CSR-1 registration",
          "FCRA consultation",
          "Annual compliance (1 year)",
          "Dedicated manager"
        ]
      }
    ],
    governmentFees: "Government fees approximately ₹5,000-15,000 including stamp duty, filing fees, and DSC costs."
  },
  
  faqs: [
    { question: "What's the difference between Section 8 and Trust/Society?", answer: "Section 8 is a company registered under Companies Act with limited liability. Trust is governed by Trust Act with trustee management. Society is membership-based under Societies Act. Section 8 has most stringent compliance but highest credibility." },
    { question: "Can Section 8 company make profits?", answer: "Section 8 can earn surplus but cannot distribute it to members. All income must be used for company's charitable objectives." },
    { question: "Is annual compliance mandatory?", answer: "Yes, Section 8 companies must comply with Companies Act requirements: annual returns, financial statements, AGM, board meetings, and audit." },
    { question: "Can Section 8 be converted from Trust/Society?", answer: "Yes, existing trusts and societies can be converted to Section 8 companies with proper legal procedures and approvals." },
    { question: "What are the annual compliance costs?", answer: "Annual compliance typically costs ₹15,000-25,000 including audit, annual filing, IT returns, and other statutory requirements." },
    { question: "Can foreign directors be appointed?", answer: "Yes, but at least one director must be Indian resident. Foreign directors need DIN and DSC with additional documentation." }
  ],
  
  relatedServices: [
    { id: "trust-registration", label: "Trust Registration" },
    { id: "society-registration", label: "Society Registration" },
    { id: "annual-filing", label: "Annual Compliance" },
    { id: "fcra-registration", label: "FCRA Registration" },
    { id: "ngo-audit", label: "NGO Audit" },
    { id: "virtual-cfo", label: "Virtual CFO" }
  ],
  
  trustSignals: {
    incorporations: "800+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

// Continue exporting all services...
